import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { RiAddLine, RiCloseLine, RiPlayFill } from 'react-icons/ri';
import styles from './BookFinishedCelebration.module.css';
import AutoBookCover, { splitTitleAndAuthor } from './AutoBookCover';

// Muted copper/amber/sage — celebratory but still bookish.
const CONFETTI_COLORS = ['#c96f4a', '#d9a05b', '#8a9a5b', '#b0713f', '#e3c088', '#a45d5d'];
const CONFETTI_COUNT = 32;

// Shown once when the last chapter of a book finishes playing. Finishing a
// book is the natural moment to start the next one, so the primary CTA feeds
// the upload loop; "Keep browsing" quietly dismisses.

function formatHours(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  if (seconds < 60) return null;
  const hours = seconds / 3600;
  if (hours < 1) return `${Math.round(seconds / 60)} minutes`;
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded % 1 === 0 ? Math.round(rounded) : rounded} hour${rounded === 1 ? '' : 's'}`;
}

// `listProgress` (optional): { listTitle, finishedCount, total, levelTitle,
// nextBook: {id, title} | null } — when the finished book belongs to a
// reading list, the celebration shows the ladder progress and the primary
// CTA becomes the next book in that list instead of the uploader.
function BookFinishedCelebration({
  title,
  coverUrl,
  totalDurationSeconds,
  onAddBook,
  onClose,
  onSeeKnowledge,
  listProgress = null,
  onPlayNext
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const { title: displayTitle } = splitTitleAndAuthor(title || '');
  const hoursLabel = formatHours(totalDurationSeconds);

  // One-shot confetti burst; randomized once per mount.
  const confetti = useMemo(() => Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + Math.random() * 5,
    round: i % 3 === 0,
    delay: `${(Math.random() * 0.7).toFixed(2)}s`,
    duration: `${(2.6 + Math.random() * 1.8).toFixed(2)}s`,
    sway: `${Math.round((Math.random() - 0.5) * 160)}px`,
    spin: `${Math.round(360 + Math.random() * 540)}deg`,
  })), []);

  return createPortal(
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label="Book finished">
      <div className={styles.confettiLayer} aria-hidden="true">
        {confetti.map((piece, index) => (
          <span
            key={index}
            className={styles.confettiPiece}
            style={{
              left: piece.left,
              width: piece.size,
              height: piece.round ? piece.size : piece.size * 1.6,
              background: piece.color,
              borderRadius: piece.round ? '50%' : 2,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
              '--confetti-sway': piece.sway,
              '--confetti-spin': piece.spin,
            }}
          />
        ))}
      </div>
      <div className={styles.card}>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
          <RiCloseLine size={20} />
        </button>

        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.coverWrap}>
          {coverUrl ? (
            <img className={styles.cover} src={coverUrl} alt="" />
          ) : (
            <AutoBookCover variant="compact" title={displayTitle} seed={displayTitle} />
          )}
          <span className={styles.badge} aria-hidden="true">✓</span>
        </div>

        <span className={styles.eyebrow}>Book finished</span>
        <h2 className={styles.title}>{displayTitle}</h2>
        <p className={styles.copy}>
          {hoursLabel
            ? <>That's <strong>{hoursLabel}</strong> of listening, cover to cover. Time for the next one?</>
            : <>Cover to cover, done. Time for the next one?</>}
        </p>

        {listProgress && (
          <div className={styles.listProgress}>
            <span className={styles.listProgressLine}>
              <strong>{listProgress.listTitle}</strong>
              {' · '}
              {listProgress.finishedCount} of {listProgress.total} finished
              {listProgress.levelTitle ? ` · ${listProgress.levelTitle}` : ''}
            </span>
            <span className={styles.listProgressTrack} aria-hidden="true">
              <span
                className={styles.listProgressFill}
                style={{
                  width: `${Math.min(100, Math.round((listProgress.finishedCount / Math.max(1, listProgress.total)) * 100))}%`
                }}
              />
            </span>
          </div>
        )}

        {listProgress?.nextBook && onPlayNext ? (
          <button
            type="button"
            className={styles.primaryCta}
            onClick={() => onPlayNext(listProgress.nextBook.id)}
          >
            <RiPlayFill size={18} />
            Next in the list · {listProgress.nextBook.title}
          </button>
        ) : null}
        {onSeeKnowledge && (
          <button
            type="button"
            className={listProgress?.nextBook ? styles.secondaryCta : styles.primaryCta}
            onClick={onSeeKnowledge}
          >
            See what you're taking with you
          </button>
        )}
        {!listProgress?.nextBook && (
          <button type="button" className={onSeeKnowledge ? styles.secondaryCta : styles.primaryCta} onClick={onAddBook}>
            <RiAddLine size={18} />
            Add another book
          </button>
        )}
        <button type="button" className={styles.secondaryCta} onClick={onClose}>
          Keep browsing
        </button>
      </div>
    </div>,
    document.body
  );
}

export default BookFinishedCelebration;
